import React, { useState, useEffect, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import "./Vehicle.css";
import Pagination from "../common/Pagination";
import axios from "axios";
import config from "../../config.js";
import activeEmoji from "../../images/vehicle/Active_icon.svg";
import inActiveEmoji from "../../images/vehicle/Inactive_icon.svg";
// import deadEmoji from "../../images/vehicle/Dead_icon.svg";
import Update_Icon from "../../images/table/Update_icon.svg";
import Track_Icon from "../../images/table/View_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import DeleteModal from "../common/DeleteModal";
import DynamicTable from "../common/DynamicTable";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";

// Helper to check if user is RouteEye Admin
const isRouteEyeAdmin = () => {
  const userState = localStorage.getItem("userState");
  if (!userState) return false;
  try {
    const user = JSON.parse(userState).user;
    return user.organizationId === 5 && user.email === "sales@routeeye.io";
  } catch {
    return false;
  }
};

// Custom headers for RouteEye Admin (with organization, site, and routes columns)
const getAdminHeaders = () => [
  { key: "vehicleName", value: "Vehicles" },
  { key: "VehicleNumber", value: "Vehicle Number" },
  { key: "organizationName", value: "Organization" },
  { key: "siteID", value: "Site" },
  { key: "routes", value: "Routes" },
  { key: "status", value: "Status" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const getHeaders = (canViewDrivers, canViewSites) => {
  const baseHeaders = [
    { key: "vehicleName", value: "Vehicles" },
    { key: "VehicleNumber", value: "Vehicle Number" },
    { key: "routes", value: "Routes" },
  ];

  console.log("Adding site column - permission check:", canViewSites);
  if (canViewSites) {
    baseHeaders.push({ key: "siteID", value: "Sites" });
  }

  // Add drivers column only if user has permission
  if (canViewDrivers) {
    baseHeaders.push({ key: "drivers", value: "Drivers" });
  }

  // Add status and actions columns
  baseHeaders.push({ key: "status", value: "Status" });
  baseHeaders.push({ key: ACTIONS_COLUMN_ID, value: "Actions" });

  return baseHeaders;
};

export default function VehicleMain() {
  const { t } = useTranslation();
  const [vehicleData, setVehicleData] = useState([]);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  // Initial filter config - will be updated based on user type
  const [filterConfig, setFilterConfig] = useState([]);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const canViewDrivers = hasPermission("/api/driver/organization", "GET");
  const canViewSites = hasPermission("/api/site/organization", "GET");

  // Choose headers based on user
  const dynamicHeaders = isRouteEyeAdmin()
    ? getAdminHeaders()
    : getHeaders(canViewDrivers, canViewSites);

  // State for route options
  const [routeOptions, setRouteOptions] = useState([]);

  // Initialize filter config based on user type
  useEffect(() => {
    if (isRouteEyeAdmin()) {
      // Admin filters: Organization, Site, Routes, Status
      setFilterConfig([
        {
          id: "organization",
          label: "Organization",
          helperText: "Select organization",
          options: organizationOptions,
        },
        {
          id: "site",
          label: "Site",
          helperText: "Select site",
          options: siteOptions,
        },
        {
          id: "route",
          label: "Routes",
          helperText: "Select route",
          options: routeOptions,
        },
        {
          id: "status",
          label: "Status",
          helperText: "Select vehicle status",
          options: [
            { value: "ACTIVE", label: "active" },
            { value: "INACTIVE", label: "inactive" },
            // { value: "DEAD", label: "inactive" },
          ],
        },
      ]);
    } else {
      // Regular user filters: Route, Status
      setFilterConfig([
        {
          id: "route",
          label: "Routes",
          helperText: "Select route",
          options: routeOptions,
        },
        {
          id: "status",
          label: "Status",
          helperText: "Select vehicle status",
          options: [
            { value: "ACTIVE", label: "active" },
            { value: "INACTIVE", label: "inactive" },
            // { value: "DEAD", label: "inactive" },
          ],
        },
      ]);
    }
  }, [organizationOptions, siteOptions, routeOptions]);

  // Generate actions based on permissions
  const getCustomActions = () => {
    const actions = [];

    if (hasPermission("/api/vehicle", "PUT")) {
      actions.push({
        id: "update",
        label: "Update",
        icon: Update_Icon,
        path: "/vehicle/add",
      });
    }

    if (hasPermission("/api/vehicle/id", "GET")) {
      actions.push({
        id: "track",
        label: "Track",
        icon: Track_Icon,
        path: "/vehicle/track",
      });
    }

    if (hasPermission("/api/vehicle", "DELETE")) {
      actions.push({
        id: "delete",
        label: "Delete",
        className: "delete",
        icon: Delete_Icon,
        path: "",
      });
    }

    return actions;
  };

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    if (isRouteEyeAdmin()) {
      fetchVehiclesForAdmin();
      fetchOrganizationsAndSites();
    } else {
      fetchVehiclesForOrganization();
    }
  }, [navigate]);

  // Fetch organizations and sites for admin
  const fetchOrganizationsAndSites = async () => {
    try {
      // Fetch organizations
      const orgResponse = await axios.get(`${config.baseURL}/organization`, {
        withCredentials: true,
      });

      let organizations = [];
      if (Array.isArray(orgResponse.data)) {
        organizations = orgResponse.data;
      } else if (Array.isArray(orgResponse.data.organizations)) {
        organizations = orgResponse.data.organizations;
      }

      const orgOptions = organizations.map((org) => ({
        value: org.id.toString(),
        label: org.name || `Organization ${org.id}`,
      }));
      setOrganizationOptions(orgOptions);

      // Fetch sites
      const siteResponse = await axios.get(`${config.baseURL}/site`, {
        withCredentials: true,
      });

      let sites = [];
      if (Array.isArray(siteResponse.data)) {
        sites = siteResponse.data;
      } else if (Array.isArray(siteResponse.data.sites)) {
        sites = siteResponse.data.sites;
      }

      const siteOptions = sites.map((site) => ({
        value: site.id.toString(),
        label: site.siteId || site.name || `Site ${site.id}`,
      }));
      setSiteOptions(siteOptions);

      // Fetch routes
      const routeResponse = await axios.get(`${config.baseURL}/route`, {
        withCredentials: true,
      });

      let routes = [];
      if (Array.isArray(routeResponse.data)) {
        routes = routeResponse.data;
      } else if (Array.isArray(routeResponse.data.routes)) {
        routes = routeResponse.data.routes;
      }

      const routeOpts = routes.map((route) => ({
        value: route.id.toString(),
        label: route.name || `Route ${route.id}`,
      }));
      setRouteOptions(routeOpts);
    } catch (error) {
      console.error("Error fetching organizations/sites/routes:", error);
    }
  };

  const fetchVehiclesForAdmin = () => {
    axios
      .get(`${config.baseURL}/vehicle`, { withCredentials: true })
      .then((response) => {
        let vehicles = [];
        if (Array.isArray(response.data)) {
          vehicles = response.data;
        } else if (Array.isArray(response.data.vehicles)) {
          vehicles = response.data.vehicles;
        }
        setVehicleData(vehicles);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
          navigate("/");
        }
      });
  };

  const fetchVehiclesForOrganization = () => {
    axios
      .get(`${config.baseURL}/vehicle/organization`, { withCredentials: true })
      .then((response) => {
        if (!Array.isArray(response.data)) {
          setVehicleData([]);
          setIsLoading(false);
          return;
        }

        setVehicleData(response.data);

        // Extract unique routes for filter
        const uniqueRoutes = [];
        const routeIds = new Set();
        response.data.forEach((vehicle) => {
          const routeId =
            vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;
          const routeName =
            vehicle.route?.name || vehicle.route?.Name || `Route ${routeId}`;
          if (routeId && !routeIds.has(routeId)) {
            routeIds.add(routeId);
            uniqueRoutes.push({
              value: routeId.toString(),
              label: routeName,
            });
          }
        });

        setRouteOptions(uniqueRoutes);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
          navigate("/");
        }
      });
  };

  const formatDataForTable = (data) => {
    return data.map((vehicle) => {
      const formattedData = {
        vehicleName: vehicle.name || "N/A",
        VehicleNumber: vehicle.VehicleNumber || "N/A",
        routes: vehicle.route?.name || "N/A",
        siteID: vehicle.site?.siteId || "N/A",
        organizationName: vehicle.organization?.name || "N/A",
        drivers: vehicle.driver
          ? `${vehicle.driver.first_name || ""} ${
              vehicle.driver.last_name || ""
            }`.trim()
          : "N/A",
        status: (
          <div className="status-emoji">
            {vehicle.status === "ACTIVE" && (
              <img src={activeEmoji} alt="ACTIVE" title="Active Vehicle" />
            )}
            {/* {vehicle.status === "INACTIVE" && (
              <img
                src={inActiveEmoji}
                alt="INACTIVE"
                title="Inactive Vehicle"
              />
            )}
            {(vehicle.status === "DEAD" || !vehicle.status) && (
              <img src={inActiveEmoji} alt="DEAD" title="Inactive Vehicle" />
            )} */}
            {(vehicle.status === "INACTIVE" ||
              vehicle.status === "DEAD" ||
              !vehicle.status) && (
              <img
                src={inActiveEmoji}
                alt="INACTIVE"
                title="Inactive Vehicle"
              />
            )}
          </div>
        ),
        id: vehicle.id,
        originalData: vehicle,
      };

      return formattedData;
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  const filteredVehicleData = vehicleData.filter((vehicle) => {
    // Admin filters: organization, site, route, status
    if (isRouteEyeAdmin()) {
      const organizationMatch =
        !filters.organization ||
        (vehicle.organization?.id &&
          String(vehicle.organization.id) === String(filters.organization));

      const siteMatch =
        !filters.site ||
        (vehicle.site?.id && String(vehicle.site.id) === String(filters.site));

      const routeId =
        vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;
      const routeMatch =
        !filters.route ||
        (routeId && String(routeId) === String(filters.route));

      let statusMatch = true;
      if (filters.status) {
        if (filters.status === "INACTIVE") {
          statusMatch =
            vehicle.status === "INACTIVE" ||
            vehicle.status === "DEAD" ||
            !vehicle.status;
        } else {
          statusMatch = vehicle.status === filters.status && vehicle.status;
        }
      }

      // if (filters.status) {
      //   if (filters.status === "DEAD") {
      //     statusMatch = vehicle.status === "DEAD" || !vehicle.status;
      //   } else {
      //     statusMatch = vehicle.status === filters.status && vehicle.status;
      //   }
      // }

      if (!organizationMatch || !siteMatch || !routeMatch || !statusMatch) {
        return false;
      }
    } else {
      // Regular user filters: route, status
      const routeId =
        vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;

      const routeMatch =
        !filters.route ||
        (routeId && String(routeId) === String(filters.route));

      let statusMatch = true;
      if (filters.status) {
        if (filters.status === "INACTIVE") {
          statusMatch =
            vehicle.status === "INACTIVE" ||
            vehicle.status === "DEAD" ||
            !vehicle.status;
        } else {
          statusMatch = vehicle.status === filters.status && vehicle.status;
        }
      }

      // if (filters.status) {
      //   if (filters.status === "DEAD") {
      //     statusMatch = vehicle.status === "DEAD" || !vehicle.status;
      //   } else {
      //     statusMatch = vehicle.status === filters.status && vehicle.status;
      //   }
      // }

      if (!routeMatch || !statusMatch) {
        return false;
      }
    }

    // Search query filter
    if (!searchQuery) {
      return true;
    }

    const query = searchQuery.toLowerCase();
    return (
      (vehicle.VehicleNumber &&
        vehicle.VehicleNumber.toLowerCase().includes(query)) ||
      (vehicle.name && vehicle.name.toLowerCase().includes(query)) ||
      (vehicle.site?.siteId &&
        vehicle.site.siteId.toLowerCase().includes(query)) ||
      (vehicle.organization?.name &&
        vehicle.organization.name.toLowerCase().includes(query)) ||
      (vehicle.route?.name &&
        vehicle.route.name.toLowerCase().includes(query)) ||
      (vehicle.driver?.first_name &&
        vehicle.driver.first_name.toLowerCase().includes(query)) ||
      (vehicle.driver?.last_name &&
        vehicle.driver.last_name.toLowerCase().includes(query)) ||
      (vehicle.status && vehicle.status.toLowerCase().includes(query))
    );
  });

  // Track all vehicles handler
  const handleTrackAllVehicles = () => {
    navigate("/vehicle/TrackAllVehicle", {
      state: { allVehicleData: vehicleData },
    });
  };

  // Add vehicle handler
  const handleAddVehicle = () => {
    if (hasPermission("/api/vehicle", "POST")) {
      navigate("/vehicle/add");
    } else {
      console.log("User doesn't have permission to add vehicles");
    }
  };

  // Delete modal handlers
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setVehicleToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (vehicleToDelete) {
      if (!hasPermission("/api/vehicle", "DELETE")) {
        console.log("User doesn't have permission to delete vehicles");
        setIsDeleteModalOpen(false);
        setVehicleToDelete(null);
        return;
      }
      try {
        const userState = localStorage.getItem("userState");
        const parsedUserState = JSON.parse(userState);
        const orgId = parsedUserState.user.organizationId;
        const response = await axios.delete(`${config.baseURL}/vehicle`, {
          data: { vehicleId: vehicleToDelete.id, organizationId: orgId },
        });
        console.log("Vehicle deleted successfully:", response.data);
        window.location.reload();
      } catch (error) {
        console.log("Error in deleting vehicle data: ", error);
        throw error;
      }
    }
    setIsDeleteModalOpen(false);
    setVehicleToDelete(null);
  };

  const handleActionClick = (actionId, rowIndex, actionObj) => {
    const vehicle =
      filteredVehicleData[rowIndex].originalData ||
      filteredVehicleData[rowIndex];

    switch (actionId) {
      case "update":
        if (hasPermission("/api/vehicle", "PUT")) {
          navigate(actionObj.path, { state: vehicle.id });
        }
        break;
      case "track":
        if (hasPermission("/api/vehicle/id", "GET")) {
          navigate(actionObj.path, { state: vehicle.id });
        }
        break;
      case "delete":
        if (hasPermission("/api/vehicle", "DELETE")) {
          setVehicleToDelete(vehicle);
          setIsDeleteModalOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const showActionsColumn =
    hasPermission("/api/vehicle", "PUT") ||
    hasPermission("/api/vehicle/id", "GET") ||
    hasPermission("/api/vehicle", "DELETE");

  const filteredHeaders = showActionsColumn
    ? dynamicHeaders
    : dynamicHeaders.filter((h) => h.key !== ACTIONS_COLUMN_ID);

  const canAddVehicle = hasPermission("/api/vehicle", "POST");

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <>
      <SubTitle
        title="Vehicles"
        path={`${orgName || "organization"} > Vehicles`}
        add={canAddVehicle ? "+ Create Vehicles" : ""}
        addPath={canAddVehicle ? "/vehicle/add" : ""}
        track={"monitorVehicles"}
        trackPath={"/vehicle/TrackAllVehicle"}
        onAddClick={canAddVehicle ? handleAddVehicle : null}
        onTrackClick={handleTrackAllVehicles}
      />

      <Cards
        cardData={[
          {
            title: "totalVehicles",
            value: vehicleData?.length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "activeVehicles",
            value:
              vehicleData?.filter((vehicle) => vehicle.status === "ACTIVE")
                .length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "inactiveVehicles",
            value:
              vehicleData?.filter(
                (vehicle) =>
                  vehicle.status === "INACTIVE" ||
                  vehicle.status === "DEAD" ||
                  !vehicle.status
              ).length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.YELLOW,
            titleColor: TEXT_COLORS.BLACK,
            valueColor: TEXT_COLORS.BLACK,
          },
          // {
          //   title: "deadVehicles",
          //   value:
          //     vehicleData?.filter(
          //       (vehicle) => vehicle.status === "DEAD" || !vehicle.status
          //     ).length || 0,
          //   backgroundColor: CARD_BACKGROUND_COLORS.RED,
          //   titleColor: TEXT_COLORS.WHITE,
          //   valueColor: TEXT_COLORS.WHITE,
          // },
        ]}
      />

      <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <Suspense fallback={<div className="loader"></div>}>
          <DynamicTable
            headers={filteredHeaders}
            data={formatDataForTable(
              isRouteEyeAdmin()
                ? filteredVehicleData || []
                : filteredVehicleData || []
            )}
            onActionClick={handleActionClick}
            actions={getCustomActions()}
            loading={isLoading}
            key={JSON.stringify(filters)}
          />
        </Suspense>
      </div>
      <div style={{ height: "20vh" }}></div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Vehicle!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {vehicleToDelete?.name || t("this vehicle")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </>
  );
}
